import OrganizerDatePicker, {
  formatDateInputValue,
  parseDateInputValue,
} from "../../../shared/components/OrganizerDatePicker";
import OrganizerTimeInput, {
  parseClockTimeToDate,
} from "../../../shared/components/OrganizerTimeInput";
import { siteTexts } from "../../../shared/i18n/siteTexts";

function extractDatePart(value: Date) {
  return new Date(
    value.getFullYear(),
    value.getMonth(),
    value.getDate(),
    0,
    0,
    0,
    0,
  );
}

function extractTimePart(value: Date) {
  return new Date(1970, 0, 1, value.getHours(), value.getMinutes(), 0, 0);
}

function combineDateAndTime(date: Date, time?: Date | null) {
  const hours = time ? time.getHours() : 0;
  const minutes = time ? time.getMinutes() : 0;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
    0,
    0,
  );
}

function formatTimeInputValue(value?: Date | null) {
  if (!value) return "";
  return `${String(value.getHours()).padStart(2, "0")}:${String(
    value.getMinutes(),
  ).padStart(2, "0")}`;
}

type AdminCreateChallengeScheduleFieldsProps = {
  isTournamentMode: boolean;
  scheduledAt: Date | null;
  scheduledEndAt: Date | null;
  setScheduledAt: (value: Date | null) => void;
  setScheduledEndAt: (value: Date | null) => void;
};

export default function AdminCreateChallengeScheduleFields({
  isTournamentMode,
  scheduledAt,
  scheduledEndAt,
  setScheduledAt,
  setScheduledEndAt,
}: AdminCreateChallengeScheduleFieldsProps) {
  const t = siteTexts.admin;
  const createTexts = t.createChallengePage;

  return (
    <>
      <div className="field">
        <span>
          {isTournamentMode
            ? createTexts.tournamentStart
            : t.labels.scheduledAt}
        </span>
        {isTournamentMode ? (
          <OrganizerDatePicker
            label={createTexts.tournamentStart}
            value={scheduledAt ? formatDateInputValue(scheduledAt) : ""}
            disabledAfter={
              scheduledEndAt ? formatDateInputValue(scheduledEndAt) : undefined
            }
            onChange={(value) => {
              const nextDate = parseDateInputValue(value);
              setScheduledAt(nextDate ? extractDatePart(nextDate) : null);
            }}
          />
        ) : (
          <div className="admin-row-two">
            <OrganizerDatePicker
              label={t.labels.date}
              value={scheduledAt ? formatDateInputValue(scheduledAt) : ""}
              onChange={(value) => {
                const nextDate = parseDateInputValue(value);
                if (!nextDate) {
                  setScheduledAt(null);
                  return;
                }
                const next = combineDateAndTime(
                  nextDate,
                  scheduledAt ? extractTimePart(scheduledAt) : null,
                );
                setScheduledAt(next);
              }}
            />
            <OrganizerTimeInput
              label={t.labels.time}
              value={formatTimeInputValue(scheduledAt)}
              onChange={(value) => {
                if (!scheduledAt) return;
                const nextTime = parseClockTimeToDate(value);
                if (!nextTime) return;
                const date = extractDatePart(scheduledAt);
                const next = combineDateAndTime(date, nextTime);
                setScheduledAt(next);
              }}
              disabled={!scheduledAt}
            />
          </div>
        )}
      </div>
      {isTournamentMode && (
        <div className="field">
          <span>{createTexts.tournamentEnd}</span>
          <OrganizerDatePicker
            label={createTexts.tournamentEnd}
            value={scheduledEndAt ? formatDateInputValue(scheduledEndAt) : ""}
            disabledBefore={
              scheduledAt ? formatDateInputValue(scheduledAt) : undefined
            }
            onChange={(value) => {
              const nextDate = parseDateInputValue(value);
              setScheduledEndAt(nextDate ? extractDatePart(nextDate) : null);
            }}
          />
        </div>
      )}
    </>
  );
}
